import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useState, useEffect, useRef, forwardRef } from "react";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import CircularProgress from "@material-ui/core/CircularProgress";
import CheckIcon from "@material-ui/icons/Check";
import CloseIcon from "@material-ui/icons/Close";
import { makeStyles } from "@material-ui/core/styles";
import { Grid } from "@material-ui/core";
import clsx from "clsx";
const useStyles = makeStyles({
    root: {
        textAlign: "center"
    },
    label: {
        minWidth: 24
    },
    startIcon: {
        "&:empty": { display: "none" }
    },
    endIcon: {
        "&:empty": { display: "none" }
    },
    errorText: {
        color: "red",
        textAlign: "center"
    },
    successText: {
        color: "green",
        textAlign: "center"
    }
});
export function usePendingState(props) {
    const [internalPending, setInternalPending] = useState(false);
    const [internalSuccess, setInternalSuccess] = useState(false);
    const [internalError, setInternalError] = useState(false);
    const [message, setMessage] = useState("");
    const { onClick } = props;
    const mountedRef = useRef(true);
    useEffect(() => () => {
        mountedRef.current = false;
    }, []);
    const handleClick = useCallback(async (e) => {
        if (onClick) {
            try {
                const maybePromise = onClick(e);
                if (maybePromise && typeof maybePromise.then === "function") {
                    setInternalPending(true);
                    await maybePromise
                        .then(res => {
                        if (!res)
                            return;
                        if (mountedRef.current) {
                            setInternalSuccess(res.ok);
                            if (res.error && res.error.message)
                                setMessage(res.error.message);
                            else if (res.data && res.data.message)
                                setMessage(res.data.message);
                            else
                                setMessage("");
                            setInternalError(!res.ok);
                        }
                    })
                        .catch(err => { });
                }
            }
            catch (e) {
                if (mountedRef.current) {
                    setInternalSuccess(false);
                    setInternalError(true);
                }
            }
            finally {
                if (mountedRef.current) {
                    setInternalPending(false);
                }
            }
        }
    }, [onClick]);
    return {
        onClick: handleClick,
        pending: props.pending ?? internalPending,
        success: props.success ?? internalSuccess,
        error: internalError || props.error,
        errorMessage: message || props.helperText
    };
}
function PendingButtonImpl(props, ref) {
    const { disabled, children, pending: _0, success: _1, error: _2, helperText: _3, hideSpinner, hideChildrenWhilePending, hideChildrenOnComplete, ...rest } = props;
    const classes = useStyles(props);
    const { pending, success, error, errorMessage, onClick } = usePendingState(props);
    const statusIcon = !hideSpinner && pending ? (_jsx(CircularProgress, { size: 24 })) : success ? (_jsx(CheckIcon, {})) : error ? (_jsx(CloseIcon, {})) : undefined;
    let { startIcon, endIcon } = props;
    let buttonLabel = children;
    if (startIcon) {
        startIcon = statusIcon ?? startIcon;
    }
    else if (endIcon) {
        endIcon = statusIcon ?? endIcon;
    }
    else {
        buttonLabel = (_jsxs(Grid, { container: true, justifyContent: "center", alignItems: "center", spacing: 1, children: [(hideChildrenWhilePending && pending) ||
                    (hideChildrenOnComplete && (success || error)) ? null : (_jsx(Grid, { item: true, style: { lineHeight: "normal" }, children: children })), statusIcon && (_jsx(Grid, { item: true, alignContent: "center", alignItems: "center", justifyContent: "center", style: { display: "flex" }, children: statusIcon }))] }));
    }
    const { successText, errorText, ...muiButtonClasses } = classes;
    return (_jsxs(_Fragment, { children: [_jsx(Button, { ref: ref, ...rest, onClick: onClick, classes: muiButtonClasses, startIcon: startIcon, endIcon: endIcon, disabled: Boolean(disabled || pending), children: buttonLabel }), errorMessage && (_jsx("div", { className: clsx({
                    [errorText]: error,
                    [successText]: !error
                }), children: errorMessage }))] }));
}
PendingButtonImpl.displayName = "PendingButton";
const PendingButton = forwardRef(PendingButtonImpl);
function PendingIconButtonImpl(props, ref) {
    const { disabled, children, pending: _0, success: _1, error: _2, ...rest } = props;
    const { pending, success, error, onClick } = usePendingState(props);
    return (_jsx(IconButton, { ref: ref, ...rest, onClick: onClick, disabled: Boolean(disabled || pending || success), children: pending ? (_jsx(CircularProgress, { color: "inherit", size: 24 })) : success ? (_jsx(CheckIcon, {})) : error ? (_jsx(CloseIcon, {})) : (children) }));
}
PendingIconButtonImpl.displayName = "PendingIconButton";
const PendingIconButton = forwardRef(PendingIconButtonImpl);
export { Button, PendingButton, PendingIconButton };
//# sourceMappingURL=Button.js.map